/**
 * ExpandableText Component
 * Reusable component for truncating text with show more/less functionality
 */

import React, { useState } from 'react';
import {
  typographyColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';

export interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
  style?: React.CSSProperties;
  showMoreText?: string;
  showLessText?: string;
  buttonStyle?: React.CSSProperties;
}

export const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  maxLines = 5,
  className = '',
  style = {},
  showMoreText = 'Show more',
  showLessText = 'Show less',
  buttonStyle = {},
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if text needs truncation by measuring line height
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const textRef = React.useRef<HTMLParagraphElement>(null);

  React.useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const computedStyle = getComputedStyle(textRef.current);
        let lineHeight = parseFloat(computedStyle.lineHeight);

        // Handle NaN or invalid lineHeight
        if (isNaN(lineHeight) || lineHeight <= 0) {
          // Fallback to fontSize * 1.2 (typical line height ratio)
          const fontSize = parseFloat(computedStyle.fontSize) || 16;
          lineHeight = fontSize * 1.2;
        }

        const maxHeight = lineHeight * maxLines;
        const tolerance = 2; // 2px tolerance
        console.log(
          'scrollHeight:',
          textRef.current.scrollHeight,
          'maxHeight:',
          maxHeight,
          'lineHeight:',
          lineHeight,
          'tolerance:',
          tolerance
        );
        setNeedsTruncation(
          textRef.current.scrollHeight > maxHeight + tolerance
        );
      }
    };

    checkTruncation();
    // Re-check on window resize
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text, maxLines]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={className} style={style}>
      <p
        ref={textRef}
        style={{
          // Custom styles from parent (takes precedence)
          ...style,
          // Truncation styles (can't be overridden)
          overflow: 'hidden',
          display: isExpanded || !needsTruncation ? 'block' : '-webkit-box',
          WebkitLineClamp: isExpanded || !needsTruncation ? 'unset' : maxLines,
          WebkitBoxOrient: 'vertical',
          transition: 'all 0.3s ease-in-out',
          // Default styles (only used if not provided in style prop)
          fontFamily: style.fontFamily || fontPrimary,
          fontSize: style.fontSize || typography.size.base,
          fontWeight: style.fontWeight || typography.weight.regular,
          color: style.color || typographyColors.primary,
          lineHeight: style.lineHeight || 'normal',
          margin: style.margin || 0,
        }}
      >
        {text}
      </p>
      {needsTruncation && (
        <button
          onClick={toggleExpanded}
          style={{
            fontSize: typography.size.sm,
            marginTop: spacing[1],
            cursor: 'pointer',
            textDecoration: 'underline',
            ...buttonStyle,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.opacity = '0.8';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          {isExpanded ? showLessText : showMoreText}
        </button>
      )}
    </div>
  );
};
